/**
 * ErrorBoundary.tsx — root-level React error boundary (T-071 / BEAD-0037)
 *
 * Before this existed, ANY uncaught render/commit error unmounted the entire React root,
 * leaving the user staring at a blank white document. The sign-out crash hit exactly this
 * path: a resolved async closure touched a removed MapLibre map, threw, and the whole app
 * disappeared instead of returning to the login screen.
 *
 * This boundary catches such an error and shows a small, recoverable fallback with a
 * "Reload" action (a fresh load re-runs the auth gate → login screen when signed out).
 * It is defence-in-depth: the underlying async guards (MapView/MapSearchBar/EVChargerLayer)
 * prevent the throw in the first place; this ensures that even an unforeseen throw degrades
 * to a recoverable screen rather than a blank page.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface for diagnostics; kept minimal so no sensitive detail is over-shared.
    console.error('Unhandled error caught by ErrorBoundary:', error, info.componentStack)
  }

  private handleReload = (): void => {
    window.location.reload()
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-background p-6 text-center"
        >
          <div>
            <h1 className="text-lg font-semibold text-foreground">Something went wrong</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              The app hit an unexpected error. Reloading usually fixes it.
            </p>
          </div>
          <button
            type="button"
            onClick={this.handleReload}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Reload
          </button>
        </div>
      )
    }

    return this.props.children
  }
}
